import { type Location, type ShouldRevalidateFunction } from '@remix-run/react';
import { useEffect, useState } from 'react';
import type { CapiPlp, GridSize } from 'types/capi/plp';

import ArrowIcon from '~/assets/icons/Arrow';
import Breadcrumbs from '~/components/BreadCrumbs';
import Content from '~/components/Content';
import HorizontalLine from '~/components/HorizontalLine';
import RecommendedArticles from '~/components/RecommendedArticles';
import PlpViewEvent from '~/events/PlpViewEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import seo from '~/utils/seo';

import Advert from './Advert';
import DirectProductsCarousel from './DirectProductsCarousel';
import FilterOptions from './FilterOptions';
import Filters from './Filters/Filters';
import NotificationTooltip from './NotificationTooltip';
import PopularReleasesCarousel from './PopularReleasesCarousel';
import Products from './Products';
import PromotionalBanner from './PromotionalBanner';
import SubMenu from './SubMenu';

function isBrandedSmPath(pathname: string) {
  return /\/sole-market\/.+$/.test(pathname);
}

function isNotIndexable(location: Location) {
  const { pathname, search } = location;

  return (
    isBrandedSmPath(pathname) ||
    (pathname.endsWith('/sole-market/') && search.includes('page=')) ||
    search.includes('gender=mens')
  );
}

export default function ProductListingPageView({
  showPromotion,
  allowNotifications = true
}: {
  allowNotifications?: boolean;
  showPromotion?: boolean;
}) {
  const plp = usePageData<CapiPlp>();

  const isUpcoming = plp.filter_options.some(
    (o) => o.text === 'Upcoming' && o.active
  );

  const hasSizeMenu = plp.menus.some((m) => m.id === 'SIZE_MENU');

  const user = useUser();
  const { clientIp, isBot } = useRootData();
  const [gridSize, setGridSize] = useState<GridSize>('small');

  useStructuredData(plp._schemas);

  useEffect(() => {
    new PlpViewEvent({ clientIp, isBot, plp, user }).send();
  }, [clientIp, plp, user, isBot]);

  const sizeSelected = plp.menus.some((m) => m.id === 'SIZE_MENU' && m.active);

  return (
    <>
      {plp.advert && <Advert advert={plp.advert} />}
      <div className="flex w-full flex-col bg-grey-tile">
        <div className="mx-auto flex w-full max-w-6xl flex-col px-4 py-2 md:px-8">
          <div className="hidden w-full pb-2 md:flex">
            <Breadcrumbs breadcrumbs={plp.breadcrumbs} />
          </div>
          <div className="grid grid-cols-3 items-center">
            <div className="col-span-full flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between md:gap-0 md:py-0">
              <h1 className="text-2xl">{plp.title}</h1>
              {plp.content && plp.content.length > 0 && (
                <div className="text-sm font-normal leading-[22px] text-grey-read md:hidden">
                  <Content content={[plp.content[0]!]} collapsibleParagraphs />
                </div>
              )}
              {plp.filter_options.length > 0 && (
                <FilterOptions options={plp.filter_options} />
              )}
            </div>
            {plp.sub_title && (
              <p className="col-span-full flex justify-center pb-2 pt-1 text-center text-sm md:col-span-2 md:justify-start md:pb-0">
                {plp.sub_title}
              </p>
            )}
          </div>
          {showPromotion && <PromotionalBanner className="py-2" />}
          {plp.content && plp.content.length > 0 && (
            <div className="hidden text-start text-sm font-normal leading-[22px] text-grey-read md:block md:w-2/3 lg:w-3/4">
              <Content content={[plp.content[0]!]} collapsibleParagraphs />
            </div>
          )}
        </div>
        <PopularReleasesCarousel />
        <div className="mx-auto w-full max-w-6xl px-4 md:px-8 md:py-1">
          <div className="flex w-full items-center justify-between gap-2">
            {plp.menus.length > 0 && (
              <div className="flex items-center gap-2 overflow-scroll pb-2 md:overflow-auto">
                <Filters menus={plp.menus} />
              </div>
            )}
            <div className={`${isUpcoming ? 'block' : 'hidden md:block'} `}>
              <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
            </div>
          </div>

          <div className="pb-4 pt-2">
            <HorizontalLine />
          </div>

          <div className="flex items-center justify-between">
            {!sizeSelected && hasSizeMenu && (
              <div className="flex max-w-[220px] items-center gap-2 whitespace-nowrap bg-[#FFAA0029] px-2 py-[5px]">
                <ArrowIcon className="rotate-90" height="16" width="16" />
                <span className="text-xs leading-5">
                  Select your size for the best results
                </span>
              </div>
            )}

            <div className={`${!isUpcoming ? 'block md:hidden' : 'hidden'}`}>
              <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
            </div>
          </div>
          {isUpcoming && allowNotifications && (
            <div className="flex justify-end">
              <NotificationTooltip />
            </div>
          )}
          <Products
            allowNotifications={allowNotifications}
            gridSize={gridSize}
          />
        </div>
        {!plp.products.length && <DirectProductsCarousel />}
      </div>
      <div className="bg-concrete">
        {plp.recent_articles && (
          <RecommendedArticles
            articles={plp.recent_articles}
            viewMoreNewsPath={plp.view_more_news_path}
          />
        )}
        {plp.content && plp.content.length > 0 && (
          <div className="mx-4 py-8 md:mx-auto md:w-2/3 lg:w-2/5 lg:py-12">
            <div className="flex flex-col gap-8">
              <Content content={plp.content.slice(1)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: CapiPlp;
};

export { loader } from './loader.server';
export const meta = ({
  data,
  location
}: {
  data?: LoaderData;
  location: Location;
}) => {
  const meta = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  if (isNotIndexable(location)) return seo.addMetaNoIndex(meta);

  return meta;
};
